import { getUserMailboxInfo } from 'owa-client-ids';
import type AttachmentPreview from 'owa-service/lib/contract/AttachmentPreview';
import getStore from '../store';
import { getAttachmentPreviewForConversations } from '../services/getAttachmentPreviewForConversations';
import type ItemId from 'owa-service/lib/contract/ItemId';
import { setConversationAttachmentPreviews } from '../mutators/mutators';

/**
 * We ideally batch together calls to fetch the attachments for many items at once into fewer calls. We can do this by
 * making any item fetching attachments wait for a timeout (currently 1000ms) while other items also make the request, and
 * once the timeout is up, the fetch batches them all into a single call. Since the results of the fetch get dropped into
 * the attachmentPreview store, components can react to them, so no need to return a promise for them
 */
const TIMEOUT_DELAY = 1000;
let idsToBeUpdated: ItemId[] = [];

export function getAttachmentPreviews(conversationId: ItemId): AttachmentPreview[] | null {
    const existingAttachmentPreviews = getStore().attachmentPreviews;

    // if attachments already loaded, give back attachment
    if (existingAttachmentPreviews.has(conversationId.Id)) {
        return existingAttachmentPreviews.get(conversationId.Id) as AttachmentPreview[];
    }

    updateAttachmentPreviews(conversationId);

    return null;
}

async function updateAttachmentPreviews(conversationId: ItemId) {
    // set the item ID in the list of IDs that need to be updated in the next batch
    idsToBeUpdated.push(conversationId);

    await delay(TIMEOUT_DELAY);

    if (idsToBeUpdated.length > 0) {
        // clear the ids so future requests don't include them unnecessarily
        const idsRequesting = idsToBeUpdated.slice();
        idsToBeUpdated = [];

        // if there are requests to be sent, send them myself, and update the promises for all those items
        const results = await getAttachmentPreviewForConversations(
            getUserMailboxInfo(),
            idsRequesting
        );

        // update all entries in the store with the promises
        idsRequesting.forEach(id => {
            const attachmentPreviews = results[id.Id];
            setConversationAttachmentPreviews(id, attachmentPreviews);
        });
    }
}

async function delay(delayTime: number) {
    await new Promise(resolve => {
        setTimeout(resolve, delayTime);
    });
}
